import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IoArrowBackCircleOutline } from "react-icons/io5";
import {
  FiUser,
  FiMapPin,
  FiLayers,
  FiShield,
  FiCheckCircle,
  FiCalendar,
  FiClock,
  FiUsers,
  FiFileText,
} from "react-icons/fi";
import { MdOutlineApartment } from "react-icons/md";
import { getApi } from "../../../../../api/api_instance";
import { API_URLS } from "../../../../../api/apiUrls";
import { Spin, Empty } from "antd";

/* ─── helpers ─── */
const fmt = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : null;

const ACTION_LABELS = {
  handle: "Authorize to Manage",
  fill: "Assign to Fill",
  manage: "Authorize to Manage",
};

const LEVEL_META = [
  {
    textColor: "text-[#06513D]",
    bg: "bg-blue-50",
    border: "border-blue-100",
    icon: <FiShield size={16} className="text-blue-600" />,
  },
  {
    textColor: "text-[#06513D]",
    bg: "bg-blue-50",
    border: "border-blue-100",
    icon: <FiShield size={16} className="text-blue-600" />,
  },
  {
    textColor: "text-[#06513D]",
    bg: "bg-sky-50",
    border: "border-sky-100",
    icon: <MdOutlineApartment size={16} className="text-sky-700" />,
  },
  {
    textColor: "text-[#06513D]",
    bg: "bg-emerald-50",
    border: "border-emerald-100",
    icon: <FiUsers size={16} className="text-emerald-600" />,
  },
];

const AVATAR_COLORS = [
  "bg-violet-100 text-violet-600",
  "bg-blue-100 text-blue-600",
  "bg-pink-100 text-pink-600",
  "bg-amber-100 text-amber-700",
  "bg-emerald-100 text-emerald-700",
];

const cleanName = (name) => {
  if (!name) return "";
  const len = name.length;
  for (let i = 1; i <= len / 2; i++) {
    const sub = name.substring(0, i);
    if (name.substring(i, i * 2) === sub) {
      return sub + name.substring(i * 2);
    }
  }
  return name;
};

const getEmployeeName = (node) => {
  if (!node) return "N/A";
  if (node.employee && node.employee.name) return node.employee.name;
  if (node.recipients?.[0]?.employeeId?.name)
    return node.recipients[0].employeeId.name;
  if (node.name) return cleanName(node.name);
  return "N/A";
};

const getEmployeeId = (node) => {
  if (!node) return "—";
  if (node.employee && node.employee.employee_id)
    return node.employee.employee_id;
  if (node.recipients?.[0]?.employeeId?.employee_id)
    return node.recipients[0].employeeId.employee_id;
  return "—";
};

const getEmployeeEmail = (node) => {
  if (!node) return "—";
  if (node.employee && node.employee.email) return node.employee.email;
  if (node.recipients?.[0]?.employeeId?.email)
    return node.recipients[0].employeeId.email;
  return "—";
};

const getAllotmentDate = (node) => {
  if (!node) return null;
  if (node.allottedAt) return node.allottedAt;
  if (node.recipients?.[0]?.allotmentDate)
    return node.recipients[0].allotmentDate;
  return null;
};

const getSectionsDisplay = (node) => {
  if (!node) return "-";
  const sections = node.recipients?.[0]?.sections;
  if (Array.isArray(sections) && sections.length > 0)
    return sections.join(", ");
  if (typeof sections === "string" && sections.trim()) return sections;
  return "-";
};

const getEndDate = (node) => {
  if (!node) return null;
  if (node.expiresAt) return node.expiresAt;
  if (node.recipients?.[0]?.endDate) return node.recipients[0].endDate;
  return null;
};

const isStatusActive = (node) => {
  if (!node) return false;
  const status = (node.status || "").toLowerCase();
  return (
    status === "active" ||
    status === "assigned" ||
    status === "form filling completed"
  );
};

const getRawStatus = (node) => {
  if (!node) return "—";
  if (node.status) return node.status;
  if (node.recipients?.[0]?.status) return node.recipients[0].status;
  return isStatusActive(node) ? "Active" : "Inactive";
};

const getScopeDisplay = (node) => {
  if (!node) return "-";
  if (node.type === "Client") return "Entire Organization";
  const dept =
    node.departmentId && typeof node.departmentId === "object"
      ? node.departmentId.name
      : null;
  const loc =
    node.locationId && typeof node.locationId === "object"
      ? node.locationId.name
      : null;
  return dept || loc || "-";
};

const getNodeRole = (node) => {
  if (!node) return "User";
  if (node.type === "Client") return "Admin";
  if (node.departmentId) return "Dept. Supervisor";
  if (node.locationId) return "Location Supervisor";
  if (node.role) {
    if (node.role === "sub_admin") return "Sub-Admin";
    if (node.role === "employee") return "Employee";
    if (node.role === "user" || node.role === "filler") return "User";
    if (node.role === "admin") return "Admin";
    return node.role.charAt(0).toUpperCase() + node.role.slice(1);
  }
  if (node.allotmentType === "fill") return "User";
  return "User";
};

const getNodeId = (node) => {
  if (!node) return "";
  const empId =
    node.employee?._id ||
    node.employeeId?._id ||
    node.recipients?.[0]?.employeeId?._id ||
    "";
  return `${node._id || ""}_${empId}`;
};

const getAssignedByLabel = (node, userMap) => {
  if (!node) return "—";
  const allottedById = node.allottedBy?.id;
  const allottedByRole = node.allottedByRole || node.allottedBy?.model || "";
  let name = "—";
  if (allottedById && userMap && userMap[allottedById]) {
    name = userMap[allottedById];
  } else if (node.allottedBy?.model === "Client") {
    name = "Client";
  }
  let role = allottedByRole;
  if (role === "Client") role = "Admin";
  if (name !== "—") return role ? `${name} (${role})` : name;
  return role || "—";
};

const getLevelName = (nodes, levelIndex) => {
  if (!nodes || nodes.length === 0) {
    if (levelIndex === 0) return "Admin";
    if (levelIndex === 1) return "Location Level";
    if (levelIndex === 2) return "Department Level";
    if (levelIndex === 3) return "User Level (Fill)";
    return "User Level (Fill)";
  }
  const node = nodes[0];
  if (node?.type === "Client") return "Admin";
  if (node?.departmentId) return "Department Level";
  if (node?.locationId) return "Location Level";
  const userRole = getNodeRole(node);
  if (
    userRole === "User" ||
    node?.role === "user" ||
    node?.role === "filler" ||
    node?.allotmentType === "fill"
  )
    return "User Level (Fill)";
  const role = node?.allottedByRole || "";
  if (role === "Client") return "Admin";
  if (role) return role;
  const type = node?.allotmentType || "";
  if (type === "handle" || type === "manage") return "Manager";
  return "User Level (Fill)";
};

const getActionLabel = (nextLevelNodes, nextLevelIndex) => {
  if (nextLevelNodes && nextLevelNodes.length > 0) {
    const type = nextLevelNodes[0]?.allotmentType;
    if (type) return ACTION_LABELS[type] || type;
  }
  if (nextLevelIndex === 3) return "Assign to Fill";
  return "Authorize to Manage";
};

const constructUserMap = (levelsData) => {
  const userMap = {};
  if (levelsData) {
    levelsData.forEach((levelNodes) => {
      levelNodes.forEach((node) => {
        const name = getEmployeeName(node);
        if (node._id) userMap[node._id] = name;
      });
    });
  }
  return userMap;
};

const flattenByLevel = (roots) => {
  const out = [];
  let cur = roots.map((n) => ({ ...n }));
  while (cur.length) {
    out.push(cur);
    const next = [];
    cur.forEach((n) => (n.children || []).forEach((c) => next.push({ ...c })));
    cur = next;
  }
  return out;
};

/* ─── WideCard — Level 1 full width ─── */
const WideCard = React.forwardRef(({ node, idx, isSelected, onClick }, ref) => {
  const av = AVATAR_COLORS[idx % AVATAR_COLORS.length];
  const active = isStatusActive(node);
  const scopeDisplay = getScopeDisplay(node);
  const role = getNodeRole(node);
  const name = getEmployeeName(node);
  const allotmentDate = getAllotmentDate(node);
  const showStatusBadge = node?.type !== "Client";

  return (
    <div
      ref={ref}
      onClick={onClick}
      className={`bg-white rounded-xl px-5 py-4 w-full max-w-[600px] cursor-pointer transition-all duration-200
        ${
          isSelected
            ? "border-2 border-[#06513D] shadow-lg shadow-blue-100"
            : "border border-gray-200 shadow-sm hover:shadow-md hover:border-blue-200"
        }`}
    >
      {/* Row 1: Avatar | Name + Role badge | Active badge */}
      <div className="flex items-center gap-3">
        <div
          className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${av}`}
        >
          <FiUser size={20} />
        </div>
        <div className="flex flex-1 items-center justify-between gap-4 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <span
              className="font-bold text-[15px] text-slate-800 truncate"
              title={name}
            >
              {name}
            </span>
            <span className="bg-[#c9e8e0] text-[#06513D] text-[11px] font-semibold px-3 py-1 rounded-full flex-shrink-0 whitespace-nowrap">
              {role}
            </span>
          </div>
          {showStatusBadge && (
            <span
              className={`text-[10px] font-bold px-3 py-1 rounded-full border flex-shrink-0 whitespace-nowrap
              ${active ? "bg-green-50 text-green-600 border-green-200" : "bg-gray-100 text-gray-500 border-gray-200"}`}
            >
              {active ? "Active" : "Inactive"}
            </span>
          )}
        </div>
      </div>

      {/* Row 2: Scope (left) + Assigned on (right) */}
      <div className="flex items-end justify-between mt-2.5 pl-14">
        <p className="text-[12px] text-slate-500">
          <span className="font-semibold text-slate-600">Scope : </span>
          {scopeDisplay}
        </p>
        {allotmentDate && (
          <div className="text-right flex-shrink-0 ml-4">
            <p className="text-[9px] text-slate-400">Assigned on</p>
            <p className="text-[11px] text-slate-600 font-bold">
              {fmt(allotmentDate)}
            </p>
          </div>
        )}
      </div>
    </div>
  );
});

/* ─── SmallCard ─── */
const SmallCard = React.forwardRef(
  ({ node, idx, isSelected, onClick }, ref) => {
    const av = AVATAR_COLORS[idx % AVATAR_COLORS.length];
    const active = isStatusActive(node);
    const scopeDisplay = getScopeDisplay(node);
    const role = getNodeRole(node);
    const name = getEmployeeName(node);
    const allotmentDate = getAllotmentDate(node);

    return (
      <div
        ref={ref}
        onClick={onClick}
        className={`bg-white rounded-xl p-3.5 w-full flex-shrink-0 cursor-pointer transition-all duration-200 flex flex-col
        ${
          isSelected
            ? "border-2 border-[#06513D] shadow-lg shadow-blue-100"
            : "border border-gray-200 shadow-sm hover:shadow-md hover:border-blue-200"
        }`}
      >
        <div className="flex items-start gap-2.5">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${av}`}
          >
            <FiUser size={17} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3">
              <span
                className="font-bold text-[13px] text-slate-800 truncate leading-snug pr-2"
                title={name}
              >
                {name}
              </span>
              <span
                className={`text-[9px] font-semibold px-2.5 py-0.5 rounded-full flex-shrink-0 border whitespace-nowrap
              ${active ? "bg-green-50 text-green-700 border-green-200" : "bg-gray-100 text-gray-500 border-gray-200"}`}
              >
                {active ? "Active" : "Inactive"}
              </span>
            </div>
            <span className="inline-block mt-1 bg-slate-100 text-slate-600 text-[10px] font-semibold px-2.5 py-0.5 rounded-full">
              {role}
            </span>
          </div>
        </div>
        <div className="flex items-end justify-between mt-3">
          <p className="text-[11px] text-slate-500 leading-snug">
            <span className="font-semibold text-slate-700">Scope : </span>
            {scopeDisplay}
          </p>
          {allotmentDate && (
            <div className="text-right flex-shrink-0 ml-2">
              <p className="text-[9px] text-slate-400 leading-none">
                Assigned on
              </p>
              <p className="text-[11px] text-slate-700 font-bold mt-0.5">
                {fmt(allotmentDate)}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  },
);

/* ─── DetailPanel ─── */
const DetailPanel = ({ node, levelNum, userMap }) => {
  const active = isStatusActive(node);
  const scopeDisplay = getScopeDisplay(node);
  const role = getNodeRole(node);
  const name = getEmployeeName(node);
  const allotmentDate = getAllotmentDate(node);
  const endDate = getEndDate(node);
  const rawStatus = getRawStatus(node);
  const assignedByLabel = getAssignedByLabel(node, userMap);

  const Row = ({ icon, label, value, badge }) => (
    <div className="flex gap-2.5 py-2.5 border-b border-slate-50 items-start">
      <span className="text-slate-400 mt-0.5 flex-shrink-0">{icon}</span>
      <div>
        <p className="text-[10px] text-black font-bold mb-0.5">{label}</p>
        {badge ? (
          <span className="bg-indigo-50 text-[#06513D] text-[10px] font-bold px-2.5 py-0.5 rounded-full">
            {value}
          </span>
        ) : (
          <p className="text-[12px] text-slate-400 font-semibold">
            {value || "—"}
          </p>
        )}
      </div>
    </div>
  );

  return (
    <div className="w-full 2xl:w-[300px] flex-shrink-0 bg-white border border-slate-200 rounded-2xl p-4 shadow-lg overflow-y-auto max-h-[90vh]">
      <div className="flex justify-between items-center mb-3.5">
        <h3 className="text-[13px] font-extrabold text-slate-800">
          Assignment Details
        </h3>
      </div>
      <div className="bg-slate-50 rounded-xl p-3 flex justify-between items-center mb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-full bg-violet-100 text-violet-600 flex items-center justify-center">
            <FiUser size={16} />
          </div>
          <div>
            <p
              className="text-[13px] font-extrabold text-slate-800"
              title={name}
            >
              {name}
            </p>
            <span className="bg-indigo-50 text-[#06513D] text-[9px] font-bold px-0.5 py-0.5 rounded-full">
              {role}
            </span>
          </div>
        </div>
        {node.type !== "Client" && (
          <span
            className={`text-[9px] font-bold px-2.5 py-1 rounded-full border
            ${active ? "bg-green-50 text-green-600 border-green-200" : "bg-gray-100 text-gray-500 border-gray-200"}`}
          >
            {active ? "Active" : "Inactive"}
          </span>
        )}
      </div>
      <Row
        icon={<FiUser size={12} />}
        label="Assigned By"
        value={assignedByLabel}
      />
      <Row
        icon={<FiShield size={12} />}
        label="Assignment Type"
        value={ACTION_LABELS[node.allotmentType] || node.allotmentType}
        badge
      />
      <Row
        icon={<FiLayers size={12} />}
        label="Level"
        value={
          node.departmentId
            ? "Department Level"
            : node.locationId
              ? "Location Level"
              : node.allotmentType === "fill" ||
                  node.role === "user" ||
                  node.role === "filler"
                ? "User Level (Fill)"
                : `Level ${levelNum}`
        }
      />
      <Row icon={<FiMapPin size={12} />} label="Scope" value={scopeDisplay} />
      <Row
        icon={<FiFileText size={12} />}
        label="Form / Section"
        value={getSectionsDisplay(node)}
      />
      <Row
        icon={<FiCalendar size={12} />}
        label="Assigned On"
        value={fmt(allotmentDate)}
      />
      {endDate && (
        <Row
          icon={<FiClock size={12} />}
          label="End Date"
          value={fmt(endDate)}
        />
      )}
      {node.type !== "Client" && (
        <Row
          icon={<FiCheckCircle size={12} />}
          label="Status"
          value={rawStatus}
        />
      )}
    </div>
  );
};

/* ─── Stats helpers ─── */
const calcStatsFromLevels = (levelsMap) => {
  let levelsCount = 0;
  let fillCount = 0;
  let manageCount = 0;
  if (levelsMap) {
    Object.keys(levelsMap).forEach((key) => {
      const list = levelsMap[key] || [];
      if (!list.length) return;
      levelsCount++;
      list.forEach((node) => {
        if (node.allotmentType === "fill") fillCount++;
        else if (
          node.allotmentType === "handle" ||
          node.allotmentType === "manage"
        )
          manageCount++;
      });
    });
  }
  return {
    levels: levelsCount,
    users: fillCount + manageCount,
    fill: fillCount,
    manage: manageCount,
  };
};

const parseLevels = (levelsData) => {
  if (!levelsData) return [];
  const sortedKeys = Object.keys(levelsData).sort((a, b) => {
    const numA = parseInt(a.replace(/^\D+/g, ""), 10) || 0;
    const numB = parseInt(b.replace(/^\D+/g, ""), 10) || 0;
    return numA - numB;
  });
  return sortedKeys
    .map((key) => levelsData[key] || [])
    .filter((nodes) => nodes.length > 0);
};

/* ─── Main Page ─── */
export default function HierarchyPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    levels: 0,
    users: 0,
    fill: 0,
    manage: 0,
  });
  const [selected, setSelected] = useState(null);
  const [connectorLefts, setConnectorLefts] = useState({});
  const chartRef = useRef(null);
  const cardRefs = useRef({});
  const cardContainerRefs = useRef({});

  const userMap = React.useMemo(() => constructUserMap(levels), [levels]);

  const calcStats = (roots) => {
    let users = 0,
      fill = 0,
      manage = 0,
      maxL = 0;
    const walk = (n, d) => {
      if (!n) return;
      users += n.recipients?.length || 0;
      if (n.allotmentType === "fill") fill++;
      if (n.allotmentType === "handle" || n.allotmentType === "manage")
        manage++;
      maxL = Math.max(maxL, d);
      (n.children || []).forEach((c) => walk(c, d + 1));
    };
    roots.forEach((r) => walk(r, 1));
    return { levels: maxL, users, fill, manage };
  };

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getApi({ apiUrl: API_URLS.GET_FORM_FLOW_TREE(id) })
      .then((res) => {
        if (res.success) {
          let parsedLevels = [];
          let calculatedStats = { levels: 0, users: 0, fill: 0, manage: 0 };
          let initialSelected = null;

          if (res.data?.levels) {
            parsedLevels = parseLevels(res.data.levels);
            calculatedStats = calcStatsFromLevels(res.data.levels);
            for (let i = 0; i < parsedLevels.length; i++) {
              if (parsedLevels[i]?.length > 0) {
                initialSelected = {
                  node: parsedLevels[i][0],
                  levelNum: i + 1,
                };
                break;
              }
            }
          } else {
            const tree = res.data?.tree || [];
            parsedLevels = flattenByLevel(tree);
            calculatedStats = calcStats(tree);
            if (tree.length > 0)
              initialSelected = { node: tree[0], levelNum: 1 };
          }

          setLevels(parsedLevels);
          setStats(calculatedStats);
          if (initialSelected) setSelected(initialSelected);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  const handleSelect = (node, levelNum) => setSelected({ node, levelNum });

  const registerCardRef = (levelIndex, cardIndex, el) => {
    if (!cardRefs.current[levelIndex]) cardRefs.current[levelIndex] = {};
    if (el) cardRefs.current[levelIndex][cardIndex] = el;
    else delete cardRefs.current[levelIndex][cardIndex];
  };

  const measureConnectorPositions = () => {
    const chartEl = chartRef.current;
    if (!chartEl) return;
    const chartRect = chartEl.getBoundingClientRect();
    const nextPositions = {};

    levels.forEach((_, li) => {
      const containerEl = cardContainerRefs.current[li];
      if (!containerEl) return;
      const containerRect = containerEl.getBoundingClientRect();
      const center =
        containerRect.left - chartRect.left + containerRect.width / 2;
      nextPositions[li] = center;
    });

    setConnectorLefts(nextPositions);
  };

  useLayoutEffect(() => {
    if (loading || levels.length === 0) return;
    const raf = window.requestAnimationFrame(measureConnectorPositions);
    return () => window.cancelAnimationFrame(raf);
  }, [levels, loading, selected]);

  useEffect(() => {
    const handleResize = () => measureConnectorPositions();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [levels]);

  const STAT_CARDS = [
    {
      label: "Total Levels",
      value: stats.levels,
      icon: <FiLayers size={20} />,
      bg: "bg-blue-50",
      text: "text-blue-600",
    },
    {
      label: "Total Users",
      value: stats.users,
      icon: <FiUser size={20} />,
      bg: "bg-green-50",
      text: "text-green-600",
    },
    {
      label: "Assign to Fill",
      value: stats.fill,
      icon: <FiCheckCircle size={20} />,
      bg: "bg-amber-50",
      text: "text-amber-600",
    },
    {
      label: "Authorize to Manage",
      value: stats.manage,
      icon: <FiShield size={20} />,
      bg: "bg-indigo-50",
      text: "text-indigo-600",
    },
  ];

  return (
    <div className="min-h-screen bg-slate-10 px-4 sm:px-6 lg:px-7 py-7 font-sans">
      <div className="w-full max-w-none">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3.5">
            <IoArrowBackCircleOutline
              onClick={() => navigate(-1)}
              className="w-9 h-9 text-[#06513D] cursor-pointer hover:scale-110 transition-transform"
            />
            <div>
              <h1 className="text-xl font-extrabold text-slate-800 leading-none">
                Form Assignment Hierarchy
              </h1>
              <p className="text-xs text-slate-400 mt-1 font-medium">
                Track assignment and delegation flow for this form
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-500 cursor-pointer hover:bg-slate-50 transition-colors"
          >
            Close ✕
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-3.5 mb-5">
          {STAT_CARDS.map((s) => (
            <div
              key={s.label}
              className="bg-white rounded-xl p-5 border border-slate-100 flex items-center gap-4 shadow-sm"
            >
              <div
                className={`w-11 h-11 rounded-xl ${s.bg} ${s.text} flex items-center justify-center flex-shrink-0 mb-7`}
              >
                {s.icon}
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">
                  {s.label}
                </p>
                <p className="text-2xl font-black text-slate-800 leading-none">
                  {s.value}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Body */}
        <div className="flex flex-col 2xl:flex-row gap-5 items-start">
          {/* Levels container */}
          <div
            ref={chartRef}
            className="w-full 2xl:flex-1 min-w-0 bg-white rounded-2xl border border-slate-100 overflow-x-auto overflow-y-hidden shadow-sm"
          >
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <Spin size="large" />
              </div>
            ) : levels.length === 0 ? (
              <div className="flex justify-center items-center h-64 w-full">
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  imageStyle={{ height: 40 }}
                  description={
                    <span className="text-xs text-slate-500 font-semibold">
                      No data
                    </span>
                  }
                  style={{ padding: "16px 0" }}
                />
              </div>
            ) : (
              levels
                .map((nodes, li) => ({ nodes, li }))
                .filter((item) => item.nodes && item.nodes.length > 0)
                .map((levelObj, idx, filteredArray) => {
                  const { nodes, li } = levelObj;
                  const meta = LEVEL_META[li % LEVEL_META.length];
                  const hasNext = idx < filteredArray.length - 1;
                  const nextLevelObj = hasNext ? filteredArray[idx + 1] : null;
                  const actionLabel = nextLevelObj
                    ? getActionLabel(nextLevelObj.nodes, nextLevelObj.li)
                    : null;
                  const MAX = 3;
                  const visible = nodes.slice(0, MAX);
                  const extra = nodes.length - MAX;

                  return (
                    <React.Fragment key={li}>
                      {/* Level row */}
                      <div
                        className={`flex flex-col lg:flex-row lg:items-center px-4 sm:px-6 lg:px-7 py-6 gap-4 lg:gap-0 ${
                          actionLabel ? "border-b border-slate-50" : ""
                        }`}
                      >
                        {/* Level label box */}
                        <div
                          className={`w-full sm:w-44 flex-shrink-0 lg:mr-7 ${meta.bg} border ${meta.border} rounded-2xl px-4 py-4`}
                        >
                          <p
                            className={`text-xs font-extrabold pl-6 ${meta.textColor} mb-2`}
                          >
                            Level {li + 1}
                          </p>
                          <div className="flex items-center gap-2 mb-1.5">
                            {meta.icon}
                            <span className="text-[13px] font-bold text-slate-800">
                              {getLevelName(nodes, li)}
                            </span>
                          </div>
                          <p className="text-[11px] text-slate-500 font-medium pl-6">
                            {nodes.length}{" "}
                            {nodes.length === 1 ? "User" : "Users"}
                          </p>
                        </div>

                        {/* ✅ Cards container — uniform grid for ALL levels */}
                        <div
                          ref={(el) => {
                            cardContainerRefs.current[li] = el;
                          }}
                          className="flex-1 min-w-0 grid gap-3"
                          style={{
                            gridTemplateColumns:
                              li === 0
                                ? // Level 1: single wide card, full width
                                  "1fr"
                                : // Level 2+: auto-fit compact cards
                                  "repeat(auto-fit, minmax(200px, 1fr))",
                          }}
                        >
                          {visible.map((node, i) =>
                            li === 0 ? (
                              <WideCard
                                key={getNodeId(node) || i}
                                node={node}
                                idx={i + li * 3}
                                isSelected={
                                  getNodeId(selected?.node) === getNodeId(node)
                                }
                                onClick={() => handleSelect(node, li + 1)}
                                ref={(el) => registerCardRef(li, i, el)}
                              />
                            ) : (
                              <SmallCard
                                key={getNodeId(node) || i}
                                node={node}
                                idx={i + li * 3}
                                isSelected={
                                  getNodeId(selected?.node) === getNodeId(node)
                                }
                                onClick={() => handleSelect(node, li + 1)}
                                ref={(el) => registerCardRef(li, i, el)}
                              />
                            ),
                          )}
                          {extra > 0 && (
                            <div className="border-2 border-dashed border-slate-200 rounded-xl flex items-center justify-center min-h-[80px] text-slate-500 font-bold text-sm cursor-pointer hover:border-blue-300 hover:text-blue-500 transition-colors text-center">
                              +{extra}
                              <br />
                              More Users
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Connector between levels */}
                      {actionLabel && (
                        <div className="relative w-full flex flex-col items-center -mt-4 mb-5 xl:-mt-6 xl:mb-4 py-0 h-[32px] xl:h-[28px]">
                          <div
                            className="absolute top-0 flex flex-col items-center"
                            style={{
                              left: `${connectorLefts[li] ?? 0}px`,
                              transform: "translateX(-50%)",
                            }}
                          >
                            <div className="w-px h-5 border-l-2 border-dashed border-blue-300" />
                            <svg
                              className="w-3 h-3 text-blue-300 -mt-0.5"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                            <span className="mt-1 bg-blue-50 border border-blue-200 text-[#06513D] text-[10px] font-bold px-4 py-1 rounded-full whitespace-nowrap tracking-wide shadow-sm">
                              {actionLabel}
                            </span>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })
            )}
          </div>

          {/* Detail panel */}
          {selected && (
            <DetailPanel
              node={selected.node}
              levelNum={selected.levelNum}
              userMap={userMap}
            />
          )}
        </div>
      </div>
    </div>
  );
}
